import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const ManageContestsInner = () => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ title: "", entry_fee: "", first_prize: "", total_spots: "", prize_distribution: [] });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchTemplates();
  }, [token, navigate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/contest-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data);
    } catch (err) {
      toast.error("Failed to load contest templates.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!form.title.trim()) { toast.warn("Contest title is required."); return false; }
    if (!form.entry_fee || isNaN(form.entry_fee) || Number(form.entry_fee) < 0) { toast.warn("Valid entry fee is required."); return false; }
    if (!form.first_prize || isNaN(form.first_prize) || Number(form.first_prize) <= 0) { toast.warn("First prize must be greater than 0."); return false; }
    if (!form.total_spots || isNaN(form.total_spots) || Number(form.total_spots) < 2) { toast.warn("Total spots must be at least 2."); return false; }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        entry_fee: Number(form.entry_fee),
        first_prize: Number(form.first_prize),
        total_spots: Number(form.total_spots),
        prize_distribution: form.prize_distribution
      };
      if (editingId) {
        await axios.put(`${API_URL}/admin/contest-templates/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Template updated successfully!");
      } else {
        await axios.post(`${API_URL}/admin/contest-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Contest template created!");
      }
      setForm({ title: "", entry_fee: "", first_prize: "", total_spots: "", prize_distribution: [] });
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This won't affect already-created contests.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/contest-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Template deleted.");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to delete template.");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ 
      title: t.title, 
      entry_fee: t.entry_fee, 
      first_prize: t.first_prize, 
      total_spots: t.total_spots,
      prize_distribution: Array.isArray(t.prize_distribution) ? t.prize_distribution : []
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPrizePool = templates.reduce((sum, t) => sum + Number(t.first_prize || 0), 0);

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>🏆 Contest Templates</div>
          <div style={S.pageSubtitle}>Global templates applied to every new match</div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.indigo }}>{templates.length}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Templates</div>
          </div>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.green }}>₹{totalPrizePool.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Total 1st Prizes</div>
          </div>
        </div>
      </div>

      {/* FORM */}
      <div style={S.card}>
        <div style={S.cardTitle}>{editingId ? "✏️ Edit Template" : "➕ Create Contest Template"}</div>
        <div style={{ fontSize: "12px", color: COLORS.textMuted, marginBottom: "20px" }}>
          Templates defined here are automatically applied when you create a new match.
        </div>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: "16px" }}>
            <label style={S.label}>Contest Title *</label>
            <input
              className="admin-input-focus"
              style={S.input}
              placeholder="e.g. Mega Grand League, Starter Contest"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Entry Fee (₹)</label>
              <input
                className="admin-input-focus"
                type="number"
                min="0"
                style={S.input}
                placeholder="e.g. 49"
                value={form.entry_fee}
                onChange={e => setForm({ ...form, entry_fee: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={S.label}>1st Prize (₹)</label>
              <input
                className="admin-input-focus"
                type="number"
                min="1"
                style={S.input}
                placeholder="e.g. 10000"
                value={form.first_prize}
                onChange={e => setForm({ ...form, first_prize: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={S.label}>Total Spots</label>
              <input
                className="admin-input-focus"
                type="number"
                min="2"
                style={S.input}
                placeholder="e.g. 200"
                value={form.total_spots}
                onChange={e => setForm({ ...form, total_spots: e.target.value })}
                required
              />
            </div>
          </div>

          {/* PRIZE DISTRIBUTION TIERS */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <label style={{ ...S.label, marginBottom: 0 }}>💰 Prize Distribution (Dream11 Style)</label>
              <button 
                type="button" 
                onClick={() => setForm({ ...form, prize_distribution: [...form.prize_distribution, { rank_from: "", rank_to: "", prize: "" }] })}
                style={{ ...S.miniBtn, background: COLORS.indigo + "20", color: COLORS.indigoLight, width: "auto", padding: "4px 10px", fontSize: "10px" }}
              >
                + Add Tier
              </button>
            </div>
            
            {form.prize_distribution.length === 0 ? (
              <div style={{ padding: "12px", border: `1px dashed ${COLORS.cardBorder}`, borderRadius: "10px", textAlign: "center", fontSize: "11px", color: COLORS.textDim }}>
                No tiers defined. Only 1st prize will be shown.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {form.prize_distribution.map((tier, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: "8px", alignItems: "center" }}>
                    <input 
                      style={{ ...S.input, padding: "8px 12px" }} 
                      placeholder="Rank From" 
                      type="number" 
                      value={tier.rank_from} 
                      onChange={e => {
                        const newDist = [...form.prize_distribution];
                        newDist[idx].rank_from = e.target.value;
                        setForm({ ...form, prize_distribution: newDist });
                      }}
                    />
                    <input 
                      style={{ ...S.input, padding: "8px 12px" }} 
                      placeholder="Rank To" 
                      type="number" 
                      value={tier.rank_to} 
                      onChange={e => {
                        const newDist = [...form.prize_distribution];
                        newDist[idx].rank_to = e.target.value;
                        setForm({ ...form, prize_distribution: newDist });
                      }}
                    />
                    <input 
                      style={{ ...S.input, padding: "8px 12px" }} 
                      placeholder="Prize Amount (₹)" 
                      type="number" 
                      value={tier.prize} 
                      onChange={e => {
                        const newDist = [...form.prize_distribution];
                        newDist[idx].prize = e.target.value;
                        // Auto-sync first prize if rank_from is 1
                        let firstPrize = form.first_prize;
                        if (newDist[idx].rank_from === "1" || newDist[idx].rank_from === 1) {
                          firstPrize = e.target.value;
                        }
                        setForm({ ...form, prize_distribution: newDist, first_prize: firstPrize });
                      }}
                    />
                    <button 
                      type="button" 
                      onClick={() => setForm({ ...form, prize_distribution: form.prize_distribution.filter((_, i) => i !== idx) })}
                      style={{ ...S.miniBtn, color: COLORS.red }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Preview */}
          {form.entry_fee && form.total_spots && (
            <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "12px 16px", marginBottom: "16px", display: "flex", gap: "24px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: COLORS.textMuted }}>
                Max Collection: <strong style={{ color: COLORS.green }}>₹{(Number(form.entry_fee) * Number(form.total_spots)).toLocaleString("en-IN")}</strong>
              </span>
              {form.first_prize && (
                <span style={{ fontSize: "12px", color: COLORS.textMuted }}>
                  Prize Pool Ratio: <strong style={{ color: COLORS.amber }}>
                    {((Number(form.first_prize) / (Number(form.entry_fee) * Number(form.total_spots))) * 100).toFixed(1)}%
                  </strong>
                </span>
              )}
            </div>
          )}

          <div style={S.btnRow}>
            {editingId && (
              <button type="button" style={S.btnCancel} onClick={() => { setEditingId(null); setForm({ title: "", entry_fee: "", first_prize: "", total_spots: "" }); }}>
                Cancel
              </button>
            )}
            <button type="submit" disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : (editingId ? "Update Template" : "Create Template")}
            </button>
          </div>
        </form>
      </div>

      {/* TEMPLATES LIST */}
      <div style={S.sectionTitle}>Active Templates ({templates.length})</div>
      {loading ? (
        <div style={S.loading}>Loading templates...</div>
      ) : templates.length === 0 ? (
        <div style={S.empty}>No contest templates yet. Create one above to get started.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
          {templates.map(t => {
            const maxPool = Number(t.entry_fee) * Number(t.total_spots);
            const ratio = maxPool > 0 ? ((Number(t.first_prize) / maxPool) * 100).toFixed(1) : "—";
            return (
              <div
                key={t.id}
                className="admin-card-hover"
                style={{
                  background: COLORS.card,
                  border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: "16px",
                  padding: "20px",
                  transition: "all 0.15s",
                  position: "relative",
                }}
              >
                <div style={{ position: "absolute", top: "12px", right: "12px", display: "flex", gap: "6px" }}>
                  <button className="admin-btn-hover" style={S.miniBtn} onClick={() => handleEdit(t)} title="Edit">✏️</button>
                  <button className="admin-btn-hover" style={{ ...S.miniBtn, color: COLORS.red }} onClick={() => handleDelete(t.id, t.title)} title="Delete">🗑️</button>
                </div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: COLORS.text, paddingRight: "60px", marginBottom: "14px" }}>
                  🏆 {t.title}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                    <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Entry Fee</div>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.text, marginTop: "4px" }}>₹{Number(t.entry_fee).toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                    <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>1st Prize</div>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.green, marginTop: "4px" }}>₹{Number(t.first_prize).toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                    <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Spots</div>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.purple, marginTop: "4px" }}>{Number(t.total_spots).toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                    <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Prize Ratio</div>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.amber, marginTop: "4px" }}>{ratio}%</div>
                  </div>
                </div>
                
                {/* Prize Distribution Preview */}
                {Array.isArray(t.prize_distribution) && t.prize_distribution.length > 0 && (
                  <div style={{ marginTop: "14px", borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: "12px" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "8px" }}>Prize Breakup:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {t.prize_distribution.map((tier, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                          <span style={{ color: COLORS.textMid }}>Rank {tier.rank_from}{tier.rank_to && tier.rank_to !== tier.rank_from ? `-${tier.rank_to}` : ""}:</span>
                          <span style={{ fontWeight: 700, color: COLORS.green }}>₹{Number(tier.prize).toLocaleString("en-IN")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ManageContests = () => (
  <ToastProvider>
    <ManageContestsInner />
  </ToastProvider>
);

export default ManageContests;
